import {HttpClient} from '@angular/common/http';
import {map} from 'rxjs/operators';
import {ServiceAnswerAny} from '../../shared/service-answer';
import {GeneralEntity} from '../generalEntity';
import {UiContext, UiSettingsService} from '../../shared/ui-settings.service';

/**
 * General service to be inherited. Contains basic CRUD methods for rest services of QuantumMaps.
 */
export class GeneralService<ENTITY extends GeneralEntity> {

  /**
   * Initializes the service.
   * @param {HttpClient} http Client to be used for http requests.
   * @param uiSettingService
   * @param {string} entityUrl The endpoint url. Used in http requests.
   * @param {string} entityName The name of the entity. This is used for logging purposes.
   * @param backEndTarget {string} Either 'mapserver' or 'rego'. Specifies the target backend.
   * If not given then value of 'mapserver' is used.
   */
  constructor(protected http: HttpClient,
              protected uiSettingService: UiSettingsService,
              private entityUrl: string,
              private entityName: string,
              private backEndTarget: 'mapserver' | 'rego' = 'mapserver') {
  }

  /**
   * Checks if the data properties of sub-ServiceAnswers are equal.
   * @param serviceAnswer The serviceAnswer whose sub-answers are inspected.
   */
  protected static isServiceAnswersDataTheSame(serviceAnswer: ServiceAnswerAny): boolean {
    // If no multiple service answers then the data is ok
    if (!serviceAnswer.serviceAnswer) {
      return true;
    }
    const dataFirst: any = serviceAnswer.serviceAnswer[0].data;
    for (let i = 1; i < serviceAnswer.serviceAnswer.length; i++) {
      const dataCompare: any = serviceAnswer.serviceAnswer[i].data;
      if (!GeneralService.isSameObjects(dataFirst, dataCompare)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Compares if two objects are equal.
   * @param object1 First to be compared.
   * @param object2 Second to be compared.
   */
  protected static isSameObjects(object1: any, object2: any): boolean {

    if ((object1 === null || object1 === undefined) &&
      (object2 === null || object2 === undefined)) {
      return true;
    }

    if ((object1 === null || object1 === undefined) ||
      (object2 === null || object2 === undefined)) {
      return false;
    }

    if (typeof object1 !== typeof object2) {
      return false;
    }

    if (Array.isArray(object1)) {
      return GeneralService.isArraysEqual(object1, object2);
    }

    if (Object.keys(object1).length !== Object.keys(object2).length) {
      return false;
    }
    for (const key of Object.keys(object1)) {

      // Ignore common timestamps
      if (
        ['createdat', 'updatedat', 'deletedat'].some(
          (value: string) => key.toLowerCase() === value)) {
        continue;
      }

      if (Object.keys(object1).indexOf(key) < 0 || Object.keys(object2).indexOf(key) < 0) {
        return false;
      }

      if (typeof object1[key] !== typeof object2[key]) {
        return false;
      }

      if (typeof object1[key] === 'object') {
        if (!GeneralService.isSameObjects(object1[key], object2[key])) {
          return false;
        }
        continue;
      }

      if (Array.isArray(object1[key])) {
        if (!GeneralService.isArraysEqual(object1[key], object2[key])) {
          return false;
        }
        continue;
      }

      if (object1[key] !== object2[key]) {
        return false;

      }
    }
    return true;
  }

  /**
   * Compare arrays.
   * @param array1 The first array.
   * @param array2 The second array.
   */
  private static isArraysEqual(array1: any[], array2: any[]) {
    if (array1 === array2) {
      return true;
    }
    if (array1 == null || array2 == null) {
      return false;
    }
    if (array1.length !== array2.length) {
      return false;
    }
    if (array1.length === 0) {
      return true;
    }
    const compareArray1 = array1.slice(0);
    const compareArray2 = array2.slice(0);

    // If objects have id field sort object based on values of id field
    // This is needed for arrays of entities to make order work
    if (compareArray1[0]['id']) {
      compareArray1.sort((object1: any, object2: any) => {
        if (typeof object1['id'] === 'number') {
          return object1['id'] - object2['id'];
        } else {
          return ('' + object1['id']).localeCompare('' + object2['id']);
        }
      });
      compareArray2.sort((object1: any, object2: any) => {
        if (typeof object1['id'] === 'number') {
          return object1['id'] - object2['id'];
        } else {
          return ('' + object1['id']).localeCompare('' + object2['id']);
        }
      });
    } else { // No id field => use default sort
      compareArray1.sort();
      compareArray2.sort();
    }
    for (let i = 0; i < compareArray1.length; ++i) {
      if (!GeneralService.isSameObjects(compareArray1[i], compareArray2[i])) {
        return false;
      }
    }
    return true;
  }

  /**
   * Returns the name of the entity from given object. Return string containing name and id. If one or the other is missing then the
   * missing one is left out.
   * @param entity The object from which the name is formed.
   * @returns {string} The name.
   */
  public static getEntityName(entity: any): string {
    const isName = Boolean(entity.name);
    const isId = Boolean(entity.id);
    return isName ?
      '"' + entity.name + '"' + +(isId ? ' (' + entity.id + ')' : '') :
      (isId ? entity.id : '');
  }

  /**
   * Return all the entities from rest.
   * @returns {Promise<ENTITY[]>} The list of entities.
   */
  public async getEntities(restParameters?: { key: string, value: string }[]): Promise<ServiceAnswerAny> {
    const serviceAnswer: ServiceAnswerAny = await this.executeHttpRequest(
      (url: string) => this.getEntitiesSingle(url),
      null,
      restParameters,
      true
    );

    return serviceAnswer;
  }

  /**
   * Returns all the entities from the selected contexts.
   * @returns {Promise<ENTITY extends GeneralEntity>} The fetched entity.
   */
  public async getEntitiesSingle(url: string): Promise<ServiceAnswerAny> {
    const serviceAnswer: ServiceAnswerAny = {isOk: null, isDataEqual: true};

    try {
      const entitiesObject = await this.http.get<ENTITY>(
        url,
        {})
        .pipe(
          map((value: any) => {
            return JSON.stringify(value);
          }))
        .toPromise();
      serviceAnswer.isOk = true;
      serviceAnswer.data = JSON.parse(entitiesObject);
      return serviceAnswer;
    } catch (error) {
      console.error(`Error when fetching entities from ${url}: ${JSON.stringify(error)}`);
      serviceAnswer.isOk = false;
      serviceAnswer.errorCode = error.status;
      serviceAnswer.errorMessage = error.message + '. Answer body: ' + JSON.stringify(error.error);
      return serviceAnswer;
    }
  }

  /**
   * Returns a single entity from each selected context (prod, dev...).
   * @param {number | string} id The id of the entity to be fetched.
   * @returns {Promise<ENTITY extends GeneralEntity>} The fetched entity.
   */
  public async getEntity(id: number | string, restParameters?: { key: string, value: string }[]): Promise<ServiceAnswerAny> {
    const serviceAnswer: ServiceAnswerAny = await this.executeHttpRequest(
      (url: string) => this.getEntitySingle(url),
      id,
      restParameters,
      true
    );
    // }
    return serviceAnswer;
  }

  /**
   * Returns a single entity from the given url.
   * @param {number | string} id The id of the entity to be fetched.
   * @returns {Promise<ENTITY extends GeneralEntity>} The fetched entity.
   */
  protected async getEntitySingle(url: string): Promise<ServiceAnswerAny> {
    const serviceAnswer: ServiceAnswerAny = {isOk: null};

    try {
      const entityObject = await this.http.get<ENTITY>(
        url,
        {})
        .pipe(
          map((value: any) => {
            return JSON.stringify(value);
          }))
        .toPromise();
      serviceAnswer.isOk = true;
      serviceAnswer.data = JSON.parse(entityObject);
      serviceAnswer.isDataEqual = true;
      return serviceAnswer;
    } catch (error) {
      console.error(`Error when fetching an entity from ${url}: ${JSON.stringify(error)}`);
      serviceAnswer.isOk = false;
      serviceAnswer.errorCode = error.status;
      serviceAnswer.errorMessage = error.message + '. Answer body: ' + JSON.stringify(error.error);
      return serviceAnswer;
    }
  }

  /**
   * Adds the given entity or entities with rest.
   * @param {ENTITY} any The entity to be added.
   * @param {UiContext} uiContext If given then this uiContext is used to determinate the url(s).
   * @returns {Promise<ServiceAnswerAny>} The answer of the rest webservice call. If the call was successful then this contains also the
   * requested data.
   */
  public async addEntity(entity: any, restParameters?: { key: string, value: string }[], uiContext?: UiContext): Promise<ServiceAnswerAny> {
    return await this.executeHttpRequest(
      (url: string) => this.addEntitySingle(entity, url),
      null,
      restParameters,
      false,
      uiContext
    );
  }

  /**
   * Adds the given entity with rest. Only one entity added.
   * @param {ENTITY} any The entity to be added.
   * @returns {Promise<ServiceAnswerAny>} The answer of the rest webservice call. If the call was successful then this contains also the
   * requested data.
   */
  protected async addEntitySingle(entity: any, url: string): Promise<ServiceAnswerAny> {

    const serviceAnswer: ServiceAnswerAny = {isOk: null};

    try {
      const entityObject = await this.http.post(
        url,
        entity,
        {
          headers: {}
        })
        .pipe(
          map((value: any) => {
            return JSON.stringify(value);
          }))
        .toPromise();
      serviceAnswer.isOk = true;
      serviceAnswer.data = JSON.parse(entityObject);
      return serviceAnswer;
    } catch (error) {
      console.error(`Error when adding ${GeneralService.getEntityName(entity)}: ${JSON.stringify(error)}`);
      serviceAnswer.isOk = false;
      serviceAnswer.errorCode = error.status;
      serviceAnswer.errorMessage = error.message + '. Answer body: ' + JSON.stringify(error.error);
      return serviceAnswer;
    }
  }

  /**
   * Removes the entity or entities with rest.
   * @param {ENTITY} any The entity to be modified.
   * @returns {Promise<ServiceAnswerAny>} The answer of the rest webservice call. If the call was successful then this contains also the
   * requested data.
   */
  public async removeEntity(entityId: number | string, restParameters?: { key: string, value: string }[]): Promise<ServiceAnswerAny> {
    return await this.executeHttpRequest(
      (url: string) => this.removeEntitySingle(url),
      entityId,
      restParameters,
      false
    );
  }

  /**
   * Removes the entity from the backend.
   * @param {number} entityId The id of the entity to be removed.
   * @returns {Promise<ServiceAnswerAny>} The outcome status of the remove operation.
   */
  async removeEntitySingle(url: string): Promise<ServiceAnswerAny> {
    const serviceAnswer: ServiceAnswerAny = {isOk: null};
    try {
      const entityObject = await this.http.delete(
        url,
        {}
      )
        .pipe(
          map((value: any) => {
            return JSON.stringify(value);
          }))
        .toPromise();
      serviceAnswer.isOk = true;
      serviceAnswer.data = JSON.parse(entityObject);
      return serviceAnswer;
    } catch (error) {
      console.error(`Error when deleting ${this.entityName} (${url}): ${JSON.stringify(error)}`);
      serviceAnswer.isOk = false;
      serviceAnswer.errorCode = error.status;
      serviceAnswer.errorMessage = error.message + '. Answer body: ' + JSON.stringify(error.error);
      return serviceAnswer;
    }
  }

  /**
   * Modifies entity or entities with rest.
   * @param {ENTITY} any The entity to be modified.
   * @returns {Promise<ServiceAnswerAny>} The answer of the rest webservice call. If the call was successful then this contains also the
   * requested data.
   */
  public async modifyEntity(entity: any, entityId: any, restParameters?: { key: string, value: string }[]): Promise<ServiceAnswerAny> {

    // const serviceAnswer: ServiceAnswer = {isOk: null, serviceAnswer: []};
    // const urlSets: string[] = this.getUrlFromSelectedContext(entityId, restParameters);
    // if (urlSets.length === 1) {
    //   return await this.modifyEntitySingle(entity, urlSets[0]);
    // } else {
    //   for (const url of urlSets) {
    //     serviceAnswer.serviceAnswer.push(await this.modifyEntitySingle(entity, url));
    //   }
    //   serviceAnswer.isOk = !serviceAnswer.serviceAnswer.some((serviceAnswerInner: ServiceAnswer) => !serviceAnswerInner.isOk);
    // }
    return await this.executeHttpRequest(
      (url: string) => this.modifyEntitySingle(entity, url),
      entityId,
      restParameters,
      false
    );
  }

  /**
   * Modifies a single entity. Sends the given entity to backend with http put method. The id in the given entity object will be the one
   * used in http request.
   * @param {ENTITY} entity The entity values to be sent to rest.
   * @returns {Promise<ServiceAnswer>} The outcome status of the http request.
   */
  async modifyEntitySingle(entity: any, url: string): Promise<ServiceAnswerAny> {
    const serviceAnswer: ServiceAnswerAny = {isOk: null};

    try {
      const entityObject = await this.http.patch(
        url,
        entity,
        {})
        .pipe(
          map((value: any) => {
            return JSON.stringify(value);
          }))
        .toPromise();
      serviceAnswer.isOk = true;
      serviceAnswer.data = JSON.parse(entityObject);
      return serviceAnswer;
    } catch (error) {
      console.error(`Error when updating ${GeneralService.getEntityName(entity)}: ${JSON.stringify(error)}`);
      serviceAnswer.isOk = false;
      serviceAnswer.errorCode = error.status;
      serviceAnswer.errorMessage = error.message + '. Answer body: ' + JSON.stringify(error.error);
      return serviceAnswer;
    }
  }

  /**
   * Returns an url string.
   * @param id The id of the target.
   * @param parameters The optional parameters.
   * @param onlyBaseUrl Return only the base url (the url of the server).
   */
  public getUrlFromSelectedContext(id: number | string,
                                   parameters?: { key: string; value: string }[],
                                   onlyBaseUrl?: boolean): string[] {
    const uiContext: UiContext = this.uiSettingService.getSelectedContext();
    return this.getUrlFromContext(id, uiContext, parameters, onlyBaseUrl);
  }

  /**
   * Returns an url string.
   * @param id The id of the target.
   * @param parameters The optional parameters.
   * @param onlyBaseUrl Return only the base url (the url of the server).
   */
  public getUrlFromContext(id: number | string,
                           uiContext: UiContext,
                           parameters?: { key: string; value: string }[],
                           onlyBaseUrl?: boolean): string[] {
    const urlArrayToBeReturned: string[] = [];
    for (const urlSet of uiContext.contextUrlSets) {
      const serverUrl: string = this.backEndTarget === 'mapserver'
        ? urlSet.mapServerUrl
        : urlSet.regoUrl;

      urlArrayToBeReturned.push(
        onlyBaseUrl ?
          serverUrl :
          `${serverUrl}${this.entityUrl}/${id ? `${id}/` : ''}${this.getUrlParameters(parameters)}`
      );
    }
    return urlArrayToBeReturned;
  }

  /**
   * Returns url parameter string. If no paramaters are given then empty string is returned.
   * @param parameters The parameters from which the url string is created.
   */
  public getUrlParameters(parameters: { key: string; value: string }[]): string {
    if (!parameters) {
      return '';
    }
    let parameterString = '';
    let isFirstParameter = true;

    for (const parameter of parameters) {
      parameterString += isFirstParameter ? '?' : '&';
      parameterString += `${parameter.key}=${parameter.value}`;
      isFirstParameter = false;
    }
    return parameterString;
  }

  /**
   * General implementation of HTTP method call. Takes a function that is executed
   * for each url fetched from the selected context.
   * @param func The function that is called for each url fetched from the currently selected context.
   * @param id The if to be used in url creation. For example may be the id of the entity to be deleted.
   * @param restParameters The rest parameter to be used when creating the url.
   * @param checkDataEquality Should the equality of different context answers be checked.
   * If not, isDataEqual is true in ServiceAnswer. If checked isDataEqual is set
   * based on the data equality.
   */
  public async executeHttpRequest(func: (url: string) => Promise<ServiceAnswerAny>,
                                  id?: number | string,
                                  restParameters?: { key: string, value: string }[],
                                  checkDataEquality: boolean = false,
                                  uiContext?: UiContext
  ): Promise<ServiceAnswerAny> {

    const serviceAnswer: ServiceAnswerAny = {isOk: null, serviceAnswer: [], isDataEqual: true};
    const urlSets: string[] = uiContext ?
      this.getUrlFromContext(id, uiContext, restParameters) :
      this.getUrlFromSelectedContext(id, restParameters);
    if (urlSets.length === 1) {
      return await func(urlSets[0]);
    } else {
      const requestPromiseTable: Promise<ServiceAnswerAny>[] = [];
      for (const url of urlSets) {
        requestPromiseTable.push(func(url));
      }
      serviceAnswer.serviceAnswer = await Promise.all(requestPromiseTable);
      serviceAnswer.isOk = !serviceAnswer.serviceAnswer.some((serviceAnswerInner: ServiceAnswerAny) => !serviceAnswerInner.isOk);

      if (checkDataEquality) {
        try {
          serviceAnswer.isDataEqual = GeneralService.isServiceAnswersDataTheSame(serviceAnswer);
        } catch (error) {
          console.error(`Data equality check failed: ${error}`);
        }
      }
      return serviceAnswer;
    }
  }
}
